import { HttpClientInterface } from 'services/http/types';
import {
  CAREERS_PAGE_SIZE,
  CMS_ALL_CAREERS_CATEGORIES_ENDPOINT,
  CMS_ALL_CAREERS_CITIES_ENDPOINT,
  CMS_ALL_CAREERS_ENDPOINT,
  CMS_ALL_CAREERS_TITLES_ENDPOINT,
  CMS_ALL_CAREERS_TYPES_ENDPOINT,
  CMS_ONE_CAREER_ENDPOINT,
} from 'config/cms/endpoints';
import {
  Career,
  CareersCategory,
  CareersCities,
  CareersCollection,
  CareersFilter,
  CareersServiceInterface,
  CareersSingleJob,
  CareersTitles,
  CareersTypes,
} from 'services/careers/types';
import { normalizeJob } from 'services/careers/helpers';
import { getPageItems } from 'utils/common/list';

interface GetAllCareers {
  data: Career[];
  meta: { filter_count: number };
}

const filterCareers = (careers: Career[], filter: CareersFilter): Career[] => {
  const { location, type, category } = filter;

  let filtered = [...careers];
  if (category.length) {
    filtered = filtered.filter((career) => {
      return career.categoryIds.some((id) => category.includes(id));
    });
  }
  if (location.length) {
    filtered = filtered.filter((career) => {
      return career.locationIds.some((id) => location.includes(id));
    });
  }
  if (type.length) {
    filtered = filtered.filter((career) => {
      return career.typeIds.some((id) => type.includes(id));
    });
  }

  return filtered;
};

const filterTitles = (careers: Career[], title: string): Career[] => {
  let searched = [...careers];
  if (title) {
    searched = searched.filter((career) => {
      return career.title === title;
    });
  }

  return searched;
};

class CareersService implements CareersServiceInterface {
  constructor(private readonly client: HttpClientInterface) {}

  private careersCache: Career[] = [];

  async getAllCareers(page: number, filter: CareersFilter, title: string): Promise<CareersCollection> {
    if (this.careersCache.length === 0) {
      const { data } = await this.client.get<GetAllCareers>(CMS_ALL_CAREERS_ENDPOINT);
      this.careersCache = (await data).data.map(normalizeJob);
    }
    const searched = filterTitles(this.careersCache, title);
    const filtered = filterCareers(searched, filter);
    const result = getPageItems(filtered, page, CAREERS_PAGE_SIZE);

    return { data: result, total: filtered.length };
  }

  async getOneJob(link: string): Promise<CareersSingleJob> {
    const { data } = await this.client.get<{ data: CareersSingleJob }>(CMS_ONE_CAREER_ENDPOINT(link));
    // @ts-ignore
    return data.data[0];
  }

  async getAllCareersCategories(): Promise<CareersCategory[]> {
    const { data } = await this.client.get<CareersCategory[]>(CMS_ALL_CAREERS_CATEGORIES_ENDPOINT);
    // @ts-ignore
    return data.data;
  }

  async getAllCareersCities(): Promise<CareersCities[]> {
    const { data } = await this.client.get<CareersCities[]>(CMS_ALL_CAREERS_CITIES_ENDPOINT);
    // @ts-ignore
    return data.data;
  }

  async getAllCareersTypes(): Promise<CareersTypes[]> {
    const { data } = await this.client.get<CareersTypes[]>(CMS_ALL_CAREERS_TYPES_ENDPOINT);
    // @ts-ignore
    return data.data;
  }

  async getAllCareersTitles(): Promise<string[]> {
    const { data } = await this.client.get<CareersTitles[]>(CMS_ALL_CAREERS_TITLES_ENDPOINT);
    // @ts-ignore
    return data.data.map((value) => value.title);
  }
}

export default CareersService;
